import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Mail, Loader2, RefreshCw, ArrowLeft, AlertTriangle, ShieldCheck } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Email-OTP sign-in step. After a successful password challenge, the backend
 * mails a 6-digit code; this panel asks the user to type it in. In dev mode
 * (placeholder Resend key) the code is surfaced inline so testers can sign in
 * without a real mail provider.
 *
 * Props:
 *   challengeToken      string  short-lived JWT returned by /auth/login
 *   deliveryTo          string  masked email e.g. "a***n@allbuildconsult.com"
 *   devOtpCode          string  visible only when backend is in placeholder mode
 *   expiresInMinutes    number  TTL hint for the user
 *   email               string  full email for context (admin sign-in shows it)
 *   onComplete({user,token})    callback after success
 *   onCancel                    return to credentials form
 */
export default function TwoFAChallenge({
  challengeToken,
  deliveryTo,
  devOtpCode,
  expiresInMinutes,
  email,
  onComplete,
  onCancel,
}) {
  const [token, setToken] = useState(challengeToken);
  const [masked, setMasked] = useState(deliveryTo || '');
  const [devCode, setDevCode] = useState(devOtpCode || '');
  const [ttl, setTtl] = useState(expiresInMinutes || 10);
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(30);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const id = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setError('');
    if (!code || code.length !== 6) {
      setError('Enter the 6-digit code we emailed you.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/auth/2fa/verify`, {
        challenge_token: token,
        code,
      });
      onComplete?.(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Verification failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setResending(true);
    try {
      const res = await axios.post(`${API}/auth/2fa/resend`, { challenge_token: token });
      setToken(res.data.challenge_token || token);
      setMasked(res.data.delivery?.masked_to || masked);
      setDevCode(res.data.dev_otp_code || '');
      setTtl(res.data.delivery?.expires_in_minutes || ttl);
      setCooldown(30);
      setCode('');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Could not resend the code.';
      setError(detail);
      const m = String(detail).match(/(\d+)\s*s/i);
      if (m) setCooldown(parseInt(m[1], 10));
    } finally {
      setResending(false);
    }
  };

  return (
    <div
      data-testid="twofa-panel"
      className="border-2 border-slate-900 bg-white rounded-sm max-w-md w-full mx-auto shadow-xl"
    >
      <div className="px-6 py-4 bg-slate-900 text-white flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Mail className="w-5 h-5 text-amber-300" />
          <h3 className="font-semibold tracking-tight" style={{ fontFamily: 'Outfit' }}>
            Verify it's you
          </h3>
        </div>
        <button
          type="button"
          onClick={onCancel}
          data-testid="twofa-cancel"
          className="text-xs text-slate-300 hover:text-white inline-flex items-center gap-1"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-4">
        <div className="text-sm text-slate-700 leading-relaxed">
          We sent a 6-digit sign-in code to{' '}
          <strong data-testid="twofa-delivery-target">{masked || email || 'your email'}</strong>.
          It expires in {ttl} minute{ttl === 1 ? '' : 's'}.
        </div>

        {devCode && (
          <div
            data-testid="twofa-dev-banner"
            className="text-xs bg-amber-50 border border-amber-200 text-amber-900 px-3 py-2 rounded-sm"
          >
            <strong>Dev mode:</strong> email delivery isn't configured yet, so the code is
            shown here for testing. Your code is{' '}
            <code
              data-testid="twofa-dev-code"
              className="font-mono font-bold tracking-widest text-base bg-amber-100 px-1.5 py-0.5 rounded-sm"
            >
              {devCode}
            </code>
            .
          </div>
        )}

        {error && (
          <div
            data-testid="twofa-error"
            className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-sm inline-flex items-center gap-1.5"
          >
            <AlertTriangle className="w-4 h-4 flex-shrink-0" /> {error}
          </div>
        )}

        <div>
          <label
            htmlFor="twofa-otp-input"
            className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1"
          >
            Sign-in code
          </label>
          <input
            ref={inputRef}
            id="twofa-otp-input"
            data-testid="twofa-totp-input"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="123456"
            className="w-44 font-mono text-2xl tracking-[0.4em] text-center px-3 py-2 border-2 border-slate-300 focus:border-slate-900 outline-none rounded-sm"
          />
        </div>

        <div className="flex items-center gap-3 flex-wrap pt-2 border-t border-slate-100">
          <button
            type="submit"
            data-testid="twofa-submit"
            disabled={submitting}
            className="px-5 py-2.5 bg-slate-900 text-white text-sm font-semibold rounded-sm hover:bg-black disabled:opacity-50 inline-flex items-center gap-2"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <ShieldCheck className="w-4 h-4" />
            )}
            Verify and sign in
          </button>
          <button
            type="button"
            onClick={handleResend}
            disabled={resending || cooldown > 0}
            data-testid="twofa-resend"
            className="text-xs text-slate-700 hover:text-slate-900 inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {resending ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <RefreshCw className="w-3.5 h-3.5" />
            )}
            {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
          </button>
        </div>
      </form>
    </div>
  );
}
